import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import NoteGoogle from "../components/NoteGoogle";
import Header from "../components/Header";
import Footer from "../components/Footer";
import GuidesLies from "../components/GuidesLies";
import { filArianeJsonLd, faqJsonLd } from "../utils/donneesStructurees";
import { trackSimulatorClick } from "../utils/analytics";
import { Quote, AlertTriangle, CheckCircle, Clock } from "lucide-react";

/**
 * Page « avis MaPrimeAdapt' ».
 *
 * Celui qui tape « ma prime adapt avis » ne cherche pas une page vitrine : il
 * se demande si le jeu en vaut la chandelle. La page répond donc d'abord aux
 * objections — délais, avance de trésorerie, AMO obligatoire — avant de parler
 * de l'accompagnement. Une page qui ne dirait que du bien ne convaincrait
 * personne, et ne retiendrait pas un lecteur venu chercher des réserves.
 *
 * AUCUN TÉMOIGNAGE N'EST INVENTÉ ICI. Ce qui est présenté comme un retour de
 * bénéficiaire doit venir d'un bénéficiaire : publier de faux avis est une
 * pratique commerciale trompeuse (art. L.121-2 et L.121-4 11° du code de la
 * consommation). Le seul élément chiffré vérifiable est la note Google, dont
 * le composant renvoie à la fiche publique.
 */

const RESERVES = [
  {
    Icone: Clock,
    titre: "« Les délais sont longs »",
    texte:
      "C'est la remarque la plus fréquente, et elle est fondée. Entre le dépôt du dossier complet et la notification de l'ANAH, comptez deux à trois mois selon les départements. Il faut y ajouter le temps de la visite, du diagnostic et des devis. Un projet qui démarre en janvier se termine rarement avant l'été.",
  },
  {
    Icone: AlertTriangle,
    titre: "« Il faut avancer l'argent »",
    texte:
      "Vrai, et c'est le point qui bloque le plus de projets. L'aide est versée après réception des travaux, sur facture. Le client règle l'artisan, puis se fait rembourser. Une avance existe dans certains cas — jusqu'à 70 % pour les ménages très modestes — mais elle se demande, elle n'est pas automatique.",
  },
  {
    Icone: CheckCircle,
    titre: "« L'AMO est obligatoire, c'est un coût de plus »",
    texte:
      "L'accompagnement est effectivement obligatoire. Mais il est pris en charge par MaPrimeAdapt' : il ne coûte rien au bénéficiaire. C'est la confusion la plus répandue, et elle fait renoncer des gens qui avaient droit à l'aide.",
  },
];

const FAQ = [
  {
    question: "MaPrimeAdapt' vaut-elle vraiment le coup ?",
    answer:
      "Pour une salle de bain adaptée facturée 8 000 €, une prise en charge à 70 % laisse 2 400 € à payer. La question n'est donc pas le montant mais le délai et l'avance de trésorerie. Si vous pouvez attendre quelques mois et avancer la somme le temps du remboursement, l'aide change tout. Si vous devez faire les travaux en urgence, elle est plus difficile à mobiliser.",
  },
  {
    question: "Combien de temps faut-il compter du début à la fin ?",
    answer:
      "Quatre à sept mois dans la majorité des cas : quelques semaines pour la visite, le diagnostic autonomie et les devis, puis deux à trois mois d'instruction par l'ANAH, puis les travaux eux-mêmes. Le versement de l'aide intervient après réception des travaux.",
  },
  {
    question: "Que se passe-t-il si je commence les travaux avant l'accord ?",
    answer:
      "Vous perdez l'aide, définitivement. Aucun acompte, aucune livraison de matériel, aucune journée de chantier avant la notification écrite de l'ANAH. C'est la règle sur laquelle il n'existe aucune dérogation, pas même pour une urgence.",
  },
  {
    question: "L'accompagnement AMO est-il payant ?",
    answer:
      "Non. L'assistance à maîtrise d'ouvrage est obligatoire et prise en charge dans le cadre de MaPrimeAdapt'. Elle ne fait l'objet d'aucune facture pour le bénéficiaire.",
  },
  {
    question: "Peut-on cumuler MaPrimeAdapt' avec d'autres aides ?",
    answer:
      "Oui, avec les aides départementales, celles des caisses de retraite et, selon les cas, l'APA ou la PCH. Le cumul se prépare : l'ordre de dépôt des dossiers a des conséquences sur les montants obtenus.",
  },
  {
    question: "Comment reconnaître un accompagnement sérieux ?",
    answer:
      "Trois signes : l'AMO est habilité par l'ANAH et vous le prouve, il vous dit explicitement de ne rien engager avant la notification, et il ne vend ni les travaux ni le matériel. Un interlocuteur qui propose de commencer tout de suite vous fait perdre l'aide.",
  },
];

const AvisMaPrimeAdapt = () => {
  return (
    <div className="min-h-screen bg-white">
      <Seo
        titre="Avis MaPrimeAdapt' : ce qu'en disent les bénéficiaires"
        description="MaPrimeAdapt' vaut-elle le coup ? Délais réels, avance de trésorerie, AMO obligatoire : les réponses aux trois questions que tout le monde se pose avant de se lancer."
        chemin="/avis-maprimeadapt"
        donnees={[
          filArianeJsonLd([
            { nom: "Accueil", url: "/" },
            { nom: "Avis MaPrimeAdapt'", url: "/avis-maprimeadapt" },
          ]),
          faqJsonLd(FAQ),
        ]}
      />
      <Header />

      <main id="contenu">
        {/* Hero */}
        <section className="bg-gradient-to-br from-orange-50 to-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <Quote className="text-orange-700 mx-auto mb-4" size={44} />
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Avis MaPrimeAdapt&apos; : ce qu&apos;en disent les bénéficiaires
              </h1>
              <p className="text-xl text-gray-700 mb-8">
                « Est-ce que ça vaut vraiment le coup ? » Avant de répondre oui, il faut regarder
                les trois reproches qu&apos;on entend le plus souvent — parce qu&apos;ils sont
                fondés.
              </p>
              <div className="flex justify-center">
                <NoteGoogle taille="grande" />
              </div>
            </div>
          </div>
        </section>

        {/* Les réserves, d'abord */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Les trois reproches les plus fréquents
              </h2>
              <p className="text-gray-700 mb-10">
                Ce sont les remarques que nous entendons le plus souvent en rendez-vous. Aucune
                n&apos;est infondée.
              </p>

              <div className="space-y-6">
                {RESERVES.map(({ Icone, titre, texte }) => (
                  <div key={titre} className="bg-white p-6 rounded-lg border border-gray-200">
                    <div className="flex items-start gap-4">
                      <Icone className="text-orange-700 flex-shrink-0 mt-1" size={24} />
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{titre}</h3>
                        <p className="text-gray-700 leading-relaxed">{texte}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Ce que la pratique montre */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Ce que 289 dossiers nous ont appris
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Le dossier qui aboutit vite n&apos;est pas celui du ménage le plus modeste ni du
                  logement le plus simple : c&apos;est celui dont les pièces sont complètes au
                  premier dépôt. Un avis d&apos;imposition manquant, et le dossier attend un mois
                  de plus.
                </p>
                <p>
                  La deuxième cause de retard est le devis. Un devis qui ne détaille pas les
                  postes, ou qui mélange travaux éligibles et non éligibles, revient corrigé. Nous
                  le relisons avant dépôt pour cette raison.
                </p>
                <p>
                  Enfin, la déception la plus fréquente ne vient pas du montant obtenu mais de ce
                  qui n&apos;avait pas été anticipé : les travaux commencés trop tôt, l&apos;avance
                  de trésorerie découverte au dernier moment, ou un artisan qui n&apos;était pas
                  informé des règles. Ces trois-là se règlent en amont, pas après.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-10">
                Les questions que l&apos;on nous pose avant de se lancer
              </h2>
              <div className="space-y-6">
                {FAQ.map((q) => (
                  <div key={q.question} className="bg-white p-6 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{q.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{q.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-br from-orange-600 to-orange-700 py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Reste à savoir si vous y avez droit
            </h2>
            <p className="text-xl text-white mb-8 max-w-2xl mx-auto">
              Quatre minutes, sans engagement : éligibilité, montant estimé de l&apos;aide et reste
              à charge.
            </p>
            <Link
              to="/simulateur"
              onClick={() => trackSimulatorClick("avis-maprimeadapt")}
              className="inline-block bg-white text-orange-700 text-xl font-bold px-12 py-5 rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
            >
              Tester mon éligibilité
            </Link>
          </div>
        </section>

        <GuidesLies slugs={["comment-obtenir-maprimeadapt", "refus-maprimeadapt-motifs-recours"]} />
      </main>

      <Footer />
    </div>
  );
};

export default AvisMaPrimeAdapt;
